/* AGHSAN: preserve native card selection across links, Quick Add and media. */
(() => {
  const cards = new WeakMap();
  const modalSelector = 'button[handle][aria-controls="hdt-quick-add-modal"], button[handle][aria-controls="hdt-quick-view-modal"]';
  const imageSelector = 'img.hdt-card-product__media--main, img.hdt-card-product__media--hover';
  const imageAttributes = ['src', 'srcset', 'sizes', 'alt', 'width', 'height'];

  function remember(card) {
    if (!cards.has(card)) {
      cards.set(card, {
        images: Array.from(card.querySelectorAll(imageSelector), image => ({
          image, attributes: imageAttributes.map(name => [name, image.getAttribute(name)])
        })),
        buttons: Array.from(card.querySelectorAll(modalSelector), button => ({
          button, disabled: button.disabled, label: button.getAttribute('aria-label'),
          text: button.querySelector('.hdt-truncate')?.textContent
        }))
      });
    }
    return cards.get(card);
  }

  function restoreMedia(card, state) {
    state.images.forEach(({ image, attributes }) => {
      attributes.forEach(([name, value]) => {
        if (value === null) image.removeAttribute(name);
        else image.setAttribute(name, value);
      });
    });
    // Native rebuildImage skips identical sources: clear its last variant cache.
    card._currentImageSrc = null;
  }

  function setState(card, state, variant, pending = false) {
    state.variant = variant;
    state.pending = pending;
    card.dataset.aghsanSwatchState = pending ? 'pending' : variant ? 'resolved' : 'unavailable';
    card._currentVariant = variant;
    card._currentId = variant?.id || null;
    card.querySelectorAll('[data-pr-url]').forEach(link => {
      // Native +N is an expansion control, not a product navigation action.
      if (link.closest('.is--swatch-link')) return;
      const url = new URL(link.href, window.location.origin);
      url.searchParams.delete('option_values');
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      link.href = url.toString();
      if (variant) link.removeAttribute('aria-disabled');
      else link.setAttribute('aria-disabled', 'true');
    });
    state.buttons.forEach(({ button, disabled, label, text }) => {
      const params = new URLSearchParams(button.getAttribute('search-params') || '');
      params.delete('option_values');
      if (variant) params.set('variant', variant.id);
      else params.delete('variant');
      button.setAttribute('search-params', params.toString());
      button.disabled = !variant || disabled;
      const caption = button.querySelector('.hdt-truncate');
      const unavailable = window.themeHDN.extras.unavailable;
      if (label !== null) button.setAttribute('aria-label', !pending && !variant ? unavailable : label);
      if (caption) caption.textContent = !pending && !variant ? unavailable : text;
    });
    // Defensive support for native single-variant forms; never submit a request.
    card.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(input => {
      input.value = variant?.id || '';
      input.disabled = !variant;
    });
  }

  document.addEventListener('click', event => {
    const card = event.target.closest('hdt-card-product');
    if (!card) return;
    const swatch = event.target.closest('hdt-swatch-card');
    if (swatch) {
      const state = remember(card);
      state.swatch = swatch;
      setState(card, state, null, true);
      return;
    }
    const state = cards.get(card);
    if (state && !state.variant && event.target.closest('[data-pr-url], ' + modalSelector)
        && !event.target.closest('.is--swatch-link')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  customElements.whenDefined('hdt-swatch-card').then(() => {
    const prototype = customElements.get('hdt-swatch-card').prototype;
    const nativeResponse = prototype.processSwatchResponse;
    prototype.processSwatchResponse = function (text) {
      const card = this.closest('hdt-card-product');
      if (!card) return;
      const state = remember(card);
      if (state.swatch && state.swatch !== this) return;
      const selected = new DOMParser().parseFromString(text, 'text/html').getElementById('selected-variant');
      const variant = selected ? JSON.parse(selected.textContent) : null;
      if (!variant) {
        // Native code dereferences null and may replace the requested Size markup.
        // Keep the clicked options intact and expose the native unavailable label.
        setState(card, state, null);
        restoreMedia(card, state);
        card.classList.remove('loading');
        return;
      }
      // Native rendering, price, availability and variant resolution remain authoritative.
      nativeResponse.call(this, text);
      setState(card, state, this._currentVariant);
      if (!variant.featured_media?.preview_image) restoreMedia(card, state);
    };
  });
})();
